// React
import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
// MUI
import { Grid, Box, Button, ButtonGroup, styled, Backdrop, Container } from "@mui/material";
import { Card, CardContent, Typography } from '@mui/material';
import Iconify from "../../../components/iconify/Iconify";
import CardMedia from '@mui/material/CardMedia';
import { CardActionArea, CardActions } from '@mui/material';
import Tab from '@mui/material/Tab';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import DirectionsBusRounded from '@mui/icons-material/DirectionsBusRounded';
// Constants
import EntityConstants from "../../../configs/EntityConstants";
import MessageContants from "../../../configs/MessageContants";
import PermissionConstants from "../../../configs/PermissionConstants";
import { STUDENT_TRANSPORT_DETAIL_TABLE_HEADS, STUDENT_SOA_TABLE_HEADS, STUDENT_INVOICE_TABLE_HEADS } from "../../../configs/TableHeaderConstants";
import profile_image from "../../../assets/images/icon-student.jpg"
// Service
import { get, post, put, gen_link } from "../../../services/FetchHelper";
import { putFile, getPresignedUrl } from "../../../services/AwsService";
import SpinService from "../../../services/SpinService";
// Utils
import {
    InitialStudentState,
    StudentSchema,
    FormFields,
    AddressFormFields
} from "./Student.utils";
// Styles
import { StyledDrawerForm } from "../../../configs/GoStyles";
//Third party
import { Form, Formik } from "formik";
import toast from "react-hot-toast";
// import { v4 as uuidv4 } from 'uuid';
// Hooks
import usePermissions from "../../../hooks/usePermissions";
// Components
import GoDrawer from "../../../components/Inputs/GoDrawer";
import GoTable from "../../../common/GoTable/GoTable";
import GoForm from "../../../components/Inputs/GoForm";
import StudentTransportDetail from "./StudentTransportDetail";
import ReceiptHeader from "../Receipts/ReceiptHeader";
import GoDialog from "../../../common/GoDialog/GoDialog";
import GoButtonGroup from "../../../components/Inputs/GoButtonGroup";

const useStyles = {
    root: {
        padding: 2,
    },
    label: {
        fontWeight: 'bold',
    },
};

const actions = [
    { icon: <DirectionsBusRounded />, name: 'Transport' },
    { icon: <DirectionsBusRounded />, name: 'Receipt' },
];

const ProfileCard = styled(Card)(({ theme }) => ({
    height: "100%",
    margin: "3%",
    [theme.breakpoints.up('lg')]: {
        maxWidth: 345,
        maxHight: 345,
    },
}));

const defaultTabs = [
    {
        name: "Transport Details",
        tableHeaders: STUDENT_TRANSPORT_DETAIL_TABLE_HEADS,
        tableData: [],
        addButton: true
    }, {
        name: "Invoices",
        tableHeaders: STUDENT_INVOICE_TABLE_HEADS,
        tableData: [],
        addButton: false
    }, {
        name: "SOA",
        tableHeaders: STUDENT_SOA_TABLE_HEADS,
        tableData: [],
        addButton: false
    }
]

const Student = ({ id, onClose }) => {
    const fileInputRef = useRef(null);
    const permissions = usePermissions();
    const spinService = SpinService();
    const [student, setStudent] = useState(InitialStudentState);
    const [formFields, setFormFields] = useState(FormFields);
    const [tabValue, setTabValue] = useState('Transport Details');
    // const [open, setOpen] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [drawerComponent, setDrawerComponent] = useState(null);
    const [drawerId, setDrawerId] = useState(null);
    const formData = new FormData();
    const [myProfile, setMyProfile] = useState(null);
    const [tabs, setTabs] = useState(defaultTabs);
    const [studentId, setStudentId] = useState(id);
    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };
    const [confirmDig, setConfirmDig] = useState({
        type: "",
        open: false,
        title: "",
        id: null
    });

    const [studentHeader, setStudentHeader] = useState({});
    const [isFormDirty, setIsFormDirty] = useState(false);
    const [dependencies, setDependencies] = useState({ schools: [] });
    const [pdfTerms, setPdfTerms] = useState([]);

    const attachOptions = (name, options) => {
        setFormFields(prevFormFields =>
            prevFormFields.map(field =>
                field.name === name && (field.type === "select" || field.type === "autocomplete")
                    ? { ...field, options: options }
                    : field
            )
        );
    };

    const handleAccountsChange = async () => {
        spinService.on();
        const response = await get(EntityConstants.ACCOUNTS, "max=all");
        const data = await response.json();
        attachOptions("credit_ledger_id", data);
        spinService.off();
    };

    const handleAcademicYearsChange = async () => {
        spinService.on();
        const response = await get(EntityConstants.ACADEMIC_YEARS, "max=all");
        const data = await response.json();
        attachOptions("academic_year_id", data);
        spinService.off();
    };

    const handleClassChange = async (school_id) => {
        spinService.on();
        const response = await get(EntityConstants.GRADES, `max=all&school_id=${school_id}`);
        const data = await response.json();
        attachOptions("class_id", data);
        spinService.off();
    };

    const handleSchoolChange = async () => {
        spinService.on();
        const response = await get(EntityConstants.SCHOOLSBYCOMPANIES);
        const data = await response.json();
        attachOptions("school_id", data);
        setDependencies((prevState) => {
            return { ...prevState, schools: data }
        });
        spinService.off();
    };

    const handleStudentChange = async (id) => {
        spinService.on();
        const response = await get(EntityConstants.STUDENTS + `/${id}`);
        const data = await response.json();
        spinService.off();
        setStudent(data);
        handleClassChange(data.school_id);
        tabDataHandle("Transport Details", data.student_transport_details);
        if (data.photo_link !== null) {
            getProfilePhoto(data.school_id, data.photo_link)
        }
    };

    const fileUpload = async (values) => {
        if (formData.get("profilePhoto") !== null) {
            const response = await putFile(`${values.school_id}/student_profiles`, formData.get("profilePhoto"));
            values.photo_link = response;
            return values;
        } else {
            return values;
        }

    };

    const handleSubmit = async (values) => {
        spinService.on();
        values = await fileUpload(values);
        const isNewStudent = values.id === undefined;
        const url = isNewStudent ? EntityConstants.STUDENTS : EntityConstants.STUDENTS + `/${values.id}`;
        const method = isNewStudent ? post : put;
        const response = await method(url, { student: values });
        if ([200, 201].indexOf(response.status) > -1) {
            toast.success(isNewStudent ? MessageContants.STUDENT_CREATED : MessageContants.STUDENT_UPDATED);
            const data = await response.json();
            setStudent(data);
            setStudentId(data.id);
            handleStudentChange(data.id);
        }
        spinService.off();
    };

    const findActionByName = (name) => {
        return actions.find((action) => action.name === name);
    };

    const handleFileSelect = () => {
        fileInputRef.current.click()
    };

    const handleFileUpload = (event) => {
        const file = event.target.files[0];
        formData.append("profilePhoto", file);
    };

    const getProfilePhoto = async (school_id, photo_link) => {
        const response = await getPresignedUrl(`${school_id}/student_profiles/${photo_link}`);
        setMyProfile(response)
    }

    const onDrawerClose = () => {
        setDrawerId(null);
        setDrawerOpen(false);
        handleStudentChange(studentId);
        handleSoaInvoiceChange(studentId);
        handleInvoiceChange(studentId);
        // setOpen(false);
    };

    const openDrawer = (action) => {
        setDrawerComponent(action);
        setDrawerOpen(true);
    };

    const handleValueChange = (e, values) => {
        if (e.target.name === "school_id") {
            handleClassChange(e.target.value);
            const school = dependencies.schools.find((item) => item.id === e.target.value);
            if (school.academic_year_id === null) {
                toast.error(MessageContants.NO_ACADEMIC_YEAR_IN_SCHOOL);
                return
            }
            if (school.credit_ledger_id === null) {
                toast.error(MessageContants.NO_CREDIT_LEDGER_IN_SCHOOL);
                return
            }
            values.academic_year_id = school.academic_year_id;
            values.credit_ledger_id = school.credit_ledger_id;
        }
    };

    const tabDataHandle = (key, value) => {
        setTabs(prevFormFields =>
            prevFormFields.map(field =>
                field.name === key
                    ? { ...field, tableData: value }
                    : field
            )
        );
    };

    const handleSoaInvoiceChange = async (id) => {
        spinService.on();
        const response = await get(EntityConstants.SOA_INVOICES_BY_STUDENT + `/${id}`);
        const data = await response.json();
        tabDataHandle("SOA", data);
        spinService.off();
    };

    const handleInvoiceChange = async (id) => {
        spinService.on();
        const response = await get(EntityConstants.INVOICES_BY_STUDENT + `/${id}`);
        const data = await response.json();
        setStudentHeader(data.student_detail)
        tabDataHandle("Invoices", data.invoice_headers);
        spinService.off();
    };

    const handleClose = async (res) => {
        if (res && confirmDig.type === "Generate Invoice") {
            spinService.on();
            const response = await post(EntityConstants.GENERATE_INVOICES_BY_STUDENT + `/${student.id}`, {});
            if (response.status === 200) {
                toast.success(MessageContants.INVOICE_GENERATES);
                onDrawerClose();
            }
            spinService.off();
        }
        if (res && confirmDig.type === "Invoices") {
            spinService.on();
            const response = await put(EntityConstants.INVOICE_HEADERS + `/${confirmDig.id}`, { invoice_header: { invoice_status: false } });
            if (response.status === 200) {
                toast.success(MessageContants.INVOICE_HEADER_UPDATED);
                handleInvoiceChange(Number(id));
            }
            spinService.off();
        }
        setConfirmDig((prevState) => {
            return { ...prevState, open: false, id: null, type: "" }
        })
    };

    const studentTerms = async (id) => {
        spinService.on();
        const response = await get(EntityConstants.STUDENT_INVOICE_TERMS + `/${id}`);
        const data = await response.json();
        setPdfTerms(data);
        spinService.off();

    }

    const handlePdfStudentFeesInvoiceChange = async (term) => {
        const response = await fetch(gen_link(`${EntityConstants.PDF_STUDENT_FEES_INVOICE}`, `student_id=${id}&term_id=${term}`));
        const pdfBlob = await response.blob();
        const pdfBlobUrl = URL.createObjectURL(pdfBlob);
        const previewWindow = window.open(pdfBlobUrl, '_blank');

        // const link = document.createElement('a');
        // link.href = gen_link(`${EntityConstants.PDF_STUDENT_FEES_INVOICE}`, `student_id=${id}&term_id=${term}`);
        // link.target = '_blank';
        // link.click();
    };

    const onSwitch = (row) => {
        if (tabValue === "Invoices" && !row.invoice_status) {
            toast.error("Unable to re-activate");
            return
        }
        if (tabValue === "Invoices") {
            if (row.is_paid) {
                toast.error("Unable to deactivate paid invoice.")
                return
            }
            setConfirmDig((prevState) => {
                return { ...prevState, title: "Are you sure you want to inactivate invoices", open: true, id: row.id, type: "Invoices" }
            })
        }
    }

    useEffect(() => {
        handleSchoolChange();
        handleAcademicYearsChange();
        handleAccountsChange();
        if (id !== undefined) {
            setStudentId(Number(id))
            handleStudentChange(Number(id));
            handleSoaInvoiceChange(Number(id));
            handleInvoiceChange(Number(id));
            studentTerms(Number(id));
            // handlePdfStudentFeesInvoiceChange(Number(id));
        }
    }, [id]);

    return (
        <React.Fragment>
            <div style={{
                display: "flex"
            }}>
                <ProfileCard>
                    <CardActionArea>
                        <CardMedia
                            component="img"
                            height="auto"
                            src={student.photo_link === null ? profile_image : myProfile}
                            alt="Student Profile"
                        />
                    </CardActionArea>
                    <CardActions sx={{
                        justifyContent: "center"
                    }} >

                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleFileUpload}
                            style={{ display: 'none' }}
                        />
                        <Button size="small" color="primary" onClick={handleFileSelect}>Upload</Button>
                    </CardActions>
                </ProfileCard>
                <Formik
                    enableReinitialize
                    initialValues={student}
                    validationSchema={StudentSchema}
                    onSubmit={(values) => handleSubmit(values)}
                >
                    {({
                        values,
                        touched,
                        errors,
                        dirty,
                        handleChange,
                        handleBlur,
                        handleSubmit,
                    }) => {
                    if (dirty !== isFormDirty) setIsFormDirty(dirty);
                    return (
                        <Form noValidate onSubmit={handleSubmit}>
                            <StyledDrawerForm>
                                <Grid
                                    container
                                    maxWidth="md"
                                    rowSpacing={1}
                                    columnSpacing={{ xs: 1, sm: 2, md: 2 }}
                                >
                                    {formFields.map((formField, index) => {
                                        return (
                                            <Grid key={index} item xs={12} sm={12} md={3} lg={3}>
                                                <GoForm
                                                    config={formField}
                                                    values={values}
                                                    touched={touched}
                                                    errors={errors}
                                                    handleChange={(e) => { handleChange(e); handleValueChange(e, values); }}
                                                />
                                            </Grid>
                                        );
                                    })}
                                </Grid>
                                <Grid
                                    container
                                    maxWidth="md"
                                    rowSpacing={1}
                                    columnSpacing={{ xs: 1, sm: 2, md: 2 }}
                                    sx={{
                                        marginTop: "3px"
                                    }}
                                >
                                    {AddressFormFields.map((formField, index) => {
                                        return (
                                            <Grid key={index} item xs={12} sm={12} md={6} lg={6}>
                                                <GoForm
                                                    config={formField}
                                                    values={values}
                                                    touched={touched}
                                                    errors={errors}
                                                    handleChange={(e) => { handleChange(e); handleValueChange(e); }}
                                                />
                                            </Grid>
                                        );
                                    })}
                                </Grid>
                                <Box sx={{ pt: 2, textAlign: "right" }}>
                                    <Button
                                        variant="contained"
                                        color="error"
                                        startIcon={<Iconify icon="eva:close-circle-outline" />}
                                        onClick={() => onClose()}
                                    >
                                        Close
                                    </Button>
                                    &nbsp;&nbsp;
                                    {((values.id === undefined && permissions.has(PermissionConstants.STUDENT_CREATE)) || (values.id !== undefined && permissions.has(PermissionConstants.STUDENT_UPDATE))) &&

                                        <Button
                                            variant="contained"
                                            startIcon={<Iconify icon="eva:save-fill" />}
                                            type="submit"
                                        >
                                            {values.id === undefined ? 'Save' : 'Update'}
                                        </Button>
                                    }
                                    &nbsp;&nbsp;
                                    {values.id !== undefined && permissions.has(PermissionConstants.RECEIPT_VIEW) &&
                                        <Button
                                            variant="contained"
                                            startIcon={<Iconify icon="eva:plus-fill" />}
                                            onClick={() => openDrawer(findActionByName('Receipt'))}
                                            disabled={dirty}
                                        >
                                            Generate Receipt
                                        </Button>
                                    }
                                    &nbsp;&nbsp;
                                    {
                                        values.id !== undefined &&
                                        permissions.has(PermissionConstants.INVOICE_CREATE) &&
                                        student.student_transport_details.length > 0 &&
                                        student.student_transport_details.filter((td) => td.status).length > 0 &&
                                        <Button
                                            variant="contained"
                                            startIcon={<Iconify icon="eva:plus-fill" />}
                                            onClick={() => setConfirmDig((prevState) => { return { ...prevState, title: "Are you sure you want to generate invoices", open: true, id: null, type: "Generate Invoice" } })}
                                            disabled={dirty}
                                        >
                                            Generate Invoices
                                        </Button>
                                    }
                                    &nbsp;&nbsp;&nbsp;
                                    {
                                        values.id !== undefined && pdfTerms.length > 0 && <GoButtonGroup options={pdfTerms} onAction={(term) => handlePdfStudentFeesInvoiceChange(term)} />
                                    }
                                </Box>
                            </StyledDrawerForm>
                        </Form>
                    )}}
                </Formik>
            </div>
            {studentId !== undefined &&
                <Box sx={{ width: '100%', typography: 'body1' }}>
                    <TabContext value={tabValue}>
                        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                            <TabList onChange={handleTabChange} aria-label="lab API tabs example">
                                {
                                    tabs.map((myTab, index) => {
                                        return (
                                            <Tab key={index} label={myTab.name} value={myTab.name} />
                                        )
                                    })
                                }
                            </TabList>
                        </Box>
                        {
                            tabs.map((myTab, index) => {
                                return (
                                    <TabPanel index={index} key={index} value={myTab.name}>
                                        {myTab.addButton &&
                                            <Box sx={{ p: 2, textAlign: "right" }}>
                                                <Button
                                                    variant="contained"
                                                    startIcon={<Iconify icon="eva:plus-fill" />}
                                                    onClick={() => openDrawer(findActionByName('Transport'))}
                                                    disabled={isFormDirty}
                                                >
                                                    New Transport
                                                </Button>
                                            </Box>
                                        }
                                        {myTab.name === "SOA" && studentId !== undefined &&
                                            <Box sx={{ p: 2, textAlign: "right" }}>
                                                <Button
                                                    variant="contained"
                                                    startIcon={<Iconify icon="typcn:download" />}
                                                    onClick={async () => {
                                                        const response = await fetch(gen_link(`${EntityConstants.STUDENT_SOA_DOWNLOAD}/${studentId}`));
                                                        const pdfBlob = await response.blob();
                                                        const pdfBlobUrl = URL.createObjectURL(pdfBlob);
                                                        window.open(pdfBlobUrl, '_blank');
                                                    }}
                                                >
                                                    Download SOA
                                                </Button>
                                            </Box>
                                        }
                                        {
                                            myTab.name === "Invoices" &&
                                            <Card>
                                                <CardContent>
                                                    <Typography variant="h6" style={{ textAlign: "center" }}>INVOICE (VAT EXEMPTED)</Typography>
                                                    <br />
                                                    <div key="1" >
                                                        <Container maxWidth="md" className={useStyles.root}>
                                                            <Grid container spacing={2}>
                                                                <Grid item xs={3}><Typography variant="subtitle1">School:</Typography></Grid>
                                                                <Grid item xs={3}>{studentHeader.school}</Grid>
                                                                <Grid item xs={3}><Typography variant="subtitle1">Fee Type:</Typography></Grid>
                                                                <Grid item xs={3}>{studentHeader.fee_type}</Grid>
                                                                <Grid item xs={3}><Typography variant="subtitle1">Student Code:</Typography></Grid>
                                                                <Grid item xs={3}>{studentHeader.student_code}</Grid>
                                                                <Grid item xs={3}><Typography variant="subtitle1">Student Name:</Typography></Grid>
                                                                <Grid item xs={3}>{studentHeader.student_name}</Grid>
                                                                <Grid item xs={3}><Typography variant="subtitle1">Class:</Typography></Grid>
                                                                <Grid item xs={3}>{studentHeader.class}</Grid>
                                                                <Grid item xs={3}><Typography variant="subtitle1">Division:</Typography></Grid>
                                                                <Grid item xs={3}>{studentHeader.division}</Grid>
                                                                <Grid item xs={3}><Typography variant="subtitle1">Zone:</Typography></Grid>
                                                                <Grid item xs={3}>{studentHeader.zone}</Grid>
                                                                <Grid item xs={3}><Typography variant="subtitle1">Transport Type:</Typography></Grid>
                                                                <Grid item xs={3}>{studentHeader.transport_type}</Grid>
                                                                <Grid item xs={3}><Typography variant="subtitle1">Academic Year:</Typography></Grid>
                                                                <Grid item xs={3}> {studentHeader.academic_year}</Grid>
                                                                <Grid item xs={3}><Typography variant="subtitle1">Parent Name:</Typography></Grid>
                                                                <Grid item xs={3}>{studentHeader.parent_name}</Grid>
                                                                <Grid item xs={3}><Typography variant="subtitle1">Other Ref:</Typography></Grid>
                                                                <Grid item xs={9}>{studentHeader.other_references}</Grid>
                                                                {/* <Grid item xs={3}><Typography variant="subtitle1"></Typography></Grid>
                                                                <Grid item xs={3}></Grid> */}
                                                                <Grid item xs={3}><Typography variant="subtitle1">Debit Ledger:</Typography></Grid>
                                                                <Grid item xs={3}> {studentHeader.debit_ledger}</Grid>
                                                                <Grid item xs={3}><Typography variant="subtitle1">Credit Ledger:</Typography></Grid>
                                                                <Grid item xs={3}>{studentHeader.credit_ledger}</Grid>
                                                                <Grid item xs={3}><Typography variant="subtitle1">Invoice Start Date:</Typography></Grid>
                                                                <Grid item xs={3}>{studentHeader.invoice_start_date}</Grid>
                                                                <Grid item xs={3}><Typography variant="subtitle1">Inv End Date:</Typography></Grid>
                                                                <Grid item xs={3}>{studentHeader.invoice_end_date}</Grid>
                                                            </Grid>
                                                        </Container>
                                                        {/* <br /> */}
                                                        {/* <Typography variant="subtitle1">Name: </Typography>
                                                        <Typography variant="body1">Value</Typography> */}
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        }
                                        <GoTable
                                            tableHeads={myTab.tableHeaders}
                                            tableDatas={myTab.tableData}
                                            totalCount={myTab.tableData.length}
                                            search={false}
                                            pagination={false}
                                            onView={(row) => { setDrawerId(row.id); openDrawer(findActionByName('Transport')); }}
                                            onSwitch={(row) => onSwitch(row)}
                                        />
                                    </TabPanel>
                                )
                            })
                        }
                    </TabContext>
                </Box>
            }
            <Backdrop open={drawerOpen} />
            {
                drawerComponent !== null && drawerOpen && <GoDrawer
                    title={drawerComponent.name}
                    open={drawerOpen}
                    onClose={() => onDrawerClose()}
                >
                    {drawerComponent.name === "Transport" && <StudentTransportDetail id={drawerId} studentId={student.id} schoolId={student.school_id} onClose={() => onDrawerClose()} />}
                    {drawerComponent.name === "Receipt" && <ReceiptHeader studentId={student.id} schoolId={student.school_id} onClose={() => onDrawerClose()} />}
                </GoDrawer>
            }
            {/* {
                permissions.has(PermissionConstants.RECEIPT_VIEW) && drawerOpen && <GoDrawer
                    minwidth="50%"
                    title="Receipt Header"
                    open={drawerOpen}
                    onClose={() => onDrawerClose()}
                >
                    <ReceiptHeader id={id} onClose={() => onDrawerClose()} />
                </GoDrawer>
            } */}

            <GoDialog
                open={confirmDig.open}
                title=""
                content={confirmDig.title}
                positive="Yes"
                negative="No"
                handleClose={(res) => handleClose(res)}
            />
        </React.Fragment>
    );
};

Student.propTypes = {
    id: PropTypes.number,
    onClose: PropTypes.any,
};

export default Student;
